import { useState, useEffect } from 'react'
import api from '../../utils/api'
import Select from 'react-select'
import SelectStyles from '../../shared/css/SelectStyle'
import Input from '../../shared/components/form/input'
import Button from '../../shared/components/Button/Button'
import FormLayout from '../../shared/components/FormLayout/FormLayout'
import useFlashMessage from '../../hooks/useFlashMessage'
import './EditColorIdentity.css'

const NO_COLOR_TYPE = { _id: null, label: 'Sem tipo', value: null }

// Mini-form de edição usado tanto dentro do catálogo de um fornecedor
// (ProviderColorCatalog) quanto no lápis da lista principal — edita só
// Nome/Referência/Tipo; tecidos e custo são associados nas telas de lote
// próprias (Associar Cor ao Tecido / Adicionar Valores).
//
// `clothId` (opcional): quando a edição é aberta a partir do card/linha de um
// tecido específico, habilita o checkbox "Referência específica para este
// tecido" — marcado, edita uma exceção em `refByCloth` só pra esse tecido;
// desmarcado, edita a `ref` padrão (vale pra todos os tecidos sem exceção).
// Sem `clothId` (ex: card "Sem tecido"), não existe tecido concreto pra ter
// exceção — o form se comporta como antes, só a `ref` padrão.
function EditColorIdentity({ id, clothId, clothName, btCancelAction, handleOnSuccess }) {
    const [token] = useState(localStorage.getItem('token') || '')
    const { setFlashMessage } = useFlashMessage()

    const [load, setLoad] = useState(false)
    const [provider, setProvider] = useState(null)
    const [name, setName] = useState('')
    const [ref, setRef] = useState('')
    const [hasOverride, setHasOverride] = useState(false)
    const [overrideRef, setOverrideRef] = useState('')
    const [colorType, setColorType] = useState(NO_COLOR_TYPE)
    const [colorTypeOptions, setColorTypeOptions] = useState([NO_COLOR_TYPE])

    useEffect(() => {
        api.get(`/color/get/${id}`, {
            headers: { Authorization: `Bearer ${JSON.parse(token)}` },
        }).then((response) => {
            const color = response.data.color
            setProvider(color.provider)
            setName(color.name)
            setRef(color.ref || '')
            setColorType(color.colorType || NO_COLOR_TYPE)
            if (clothId) {
                const existing = (color.refByCloth || []).find((e) => (e.cloth?._id || e.cloth) === clothId)
                setHasOverride(!!existing)
                setOverrideRef(existing ? (existing.ref || '') : (color.ref || ''))
            }
            setLoad(true)
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id])

    function toggleOverride(checked) {
        setHasOverride(checked)
        if (checked && !overrideRef) setOverrideRef(ref)
    }

    useEffect(() => {
        api.get('/colortype/getall', {
            headers: { Authorization: `Bearer ${JSON.parse(token)}` },
        }).then((response) => {
            setColorTypeOptions([NO_COLOR_TYPE, ...response.data.colorTypes])
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    async function submit(e) {
        e.preventDefault()

        const nameValue = name.trim()
        if (!nameValue) {
            setFlashMessage('O nome da cor é obrigatório', 'error')
            return
        }

        const colorTypeToSend = colorType && colorType._id ? colorType : null

        let msgType = 'success'
        const data = await api.patch(`/color/update/${id}`, {
            name: nameValue,
            ref,
            provider,
            colorType: colorTypeToSend,
            ...(clothId ? { cloth: clothId, refOverride: hasOverride, overrideRef } : {}),
        }, {
            headers: { Authorization: `Bearer ${JSON.parse(token)}` },
        }).then((response) => response.data)
            .catch((err) => { msgType = 'error'; return err.response.data })

        setFlashMessage(data.message, msgType)

        if (msgType !== 'error') {
            handleOnSuccess()
        }
    }

    if (!load) {
        return <div className="pg-loading">Carregando cor...</div>
    }

    return (
        <FormLayout
            title="Editar Cor"
            onSubmit={submit}
            actions={
                <>
                    <Button variant="save" type="submit">Salvar</Button>
                    <Button variant="cancel" type="button" onClick={btCancelAction}>Cancelar</Button>
                </>
            }
        >
            <div className="fl-row">
                <div className="fl-col-wide form-group">
                    <Input
                        text="Nome da cor"
                        type="text"
                        name="name"
                        value={name}
                        handleOnChange={(e) => setName(e.target.value)}
                    />
                </div>
            </div>
            <div className="fl-row">
                <div className="fl-col-mid form-group">
                    <Input
                        text={hasOverride ? `Referência (só em ${clothName || 'este tecido'})` : 'Referência'}
                        type="text"
                        name="ref"
                        value={hasOverride ? overrideRef : ref}
                        handleOnChange={(e) => (hasOverride ? setOverrideRef(e.target.value) : setRef(e.target.value))}
                    />
                    {clothId && (
                        <label className="eci-override-check">
                            <input
                                type="checkbox"
                                checked={hasOverride}
                                onChange={(e) => toggleOverride(e.target.checked)}
                            />
                            Referência específica para este tecido
                        </label>
                    )}
                </div>
                <div className="fl-col-mid form-group">
                    <label className="input-label">Tipo de Cor:</label>
                    <Select
                        options={colorTypeOptions}
                        styles={SelectStyles}
                        onChange={setColorType}
                        value={colorType}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                    />
                </div>
            </div>
        </FormLayout>
    )
}

export default EditColorIdentity
